import { useState } from 'react';
import useSWR from 'swr';

import { normalizeTrustCheckEntry } from '../trustCheckLogs';

/**
 * Shapes the /v2/logs/* response into the row format CallLogs renders.
 *
 * The backend returns a single `logs` array already globally ordered
 * (newest-first) and paginated across meetings + trust-checks, each item
 * tagged with `callType`. We map each item to its display row by type — no
 * client-side merge or sort (that moved to the SQL UNION). Pure — exported
 * for testing.
 */
export function formatMeetingResponse(data) {
    const logs = Array.isArray(data.logs) ? data.logs : [];
    const entries = logs.map((item, index) => {
        if (item.callType === 'trust_check') {
            return normalizeTrustCheckEntry(item, index);
        }
        const meeting = item.meeting || {
            id: 'unknown',
            uuid: 'unknown',
            topic: 'Untitled Meeting',
            start_time: '',
            organization: 'Unknown',
        };
        const participant = item.participant || { poly_user_id: 'unknown', join_time: '' };
        return {
            log_id: `${meeting.id}-${index}`,
            meeting_id: meeting.id,
            meeting_uuid: meeting.uuid,
            callType: 'meeting',
            timestamp: meeting.start_time,
            start_time: meeting.start_time,
            title: meeting.topic,
            // Source column: meeting host_name for meetings.
            source: meeting.host_name || '',
            participant,
            organization: meeting.organization,
            meeting,
            user_name: item.user_name || '',
        };
    });
    return { ...data, entries };
}

// Lazily-loaded mock response builder. Imported only when ?mock=1 is set.
// Module-level so the dynamic import isn't re-issued per render.
async function loadMockResponse() {
    const fixtures = await import('./__tests__/__fixtures__/trustCheckLogs');
    return fixtures.mockMixedLogsResponse;
}

function buildFetchers(api_client, mockMode) {
    const fetchUserLogs = async ([_accountId, page, pageSize, _viewMode, search, appId, logTypes]) => {
        if (mockMode) return formatMeetingResponse(await loadMockResponse());
        const queryParams = { page, page_size: pageSize };
        if (search) queryParams.search = search;
        if (appId) queryParams.app_id = appId;
        if (logTypes) queryParams.log_types = logTypes;
        const response = await api_client.get('/v2/logs/me', { params: queryParams });
        if (!response.data) throw new Error(`Error fetching logs: ${response.status}`);
        return formatMeetingResponse(response.data);
    };

    const fetchAccountLogs = async ([accountId, page, pageSize, _viewMode, search, appId, logTypes]) => {
        if (mockMode) return formatMeetingResponse(await loadMockResponse());
        const queryParams = { page, page_size: pageSize };
        if (search) queryParams.search = search;
        if (appId) queryParams.app_id = appId;
        if (logTypes) queryParams.log_types = logTypes;
        const response = await api_client.get(`/v2/logs/account/${accountId}`, {
            params: queryParams,
        });
        if (!response.data) throw new Error(`Error fetching account logs: ${response.status}`);
        return formatMeetingResponse(response.data);
    };

    return { fetchUserLogs, fetchAccountLogs };
}

/**
 * Wraps SWR + the user/account fetcher selection + the 403-driven
 * accountLinkRequired flag in a single hook. Returns the shaped rows
 * plus pagination + loading flags so CallLogs renders straight from it.
 */
export function useCallLogsData({
    api_client,
    polyAccountId,
    page,
    rowsPerPage,
    viewMode,
    isAdmin,
    debouncedSearchTerm,
    logTypes,
    mockMode,
    appId,
}) {
    const [accountLinkRequired, setAccountLinkRequired] = useState(false);

    const { fetchUserLogs, fetchAccountLogs } = buildFetchers(api_client, mockMode);

    // Send log_types only when the user narrowed to a single source; both
    // selected (or unset) omits the param so the backend returns everything.
    const logTypesParam = logTypes && logTypes.length === 1 ? logTypes[0] : undefined;

    const useAccountView = viewMode === 'account' && isAdmin;
    const swrKey = useAccountView
        ? [polyAccountId, page, rowsPerPage, 'account', debouncedSearchTerm || undefined, appId || undefined, logTypesParam]
        : [polyAccountId, page, rowsPerPage, 'user', debouncedSearchTerm || undefined, appId || undefined, logTypesParam];

    const { data: logPages, error, isLoading: loading, isValidating, mutate } = useSWR(
        swrKey,
        useAccountView ? fetchAccountLogs : fetchUserLogs,
        {
            fallbackData: {
                logs: [],
                pagination: { total: 0, page: 1, page_size: rowsPerPage, total_pages: 1 },
            },
            onError: (err) => {
                if (err?.response?.status === 403) {
                    setAccountLinkRequired(true);
                }
            },
            onErrorRetry: (err) => {
                if (err?.response?.status === 403) return;
            },
        }
    );

    // entries is the per-type display row list mapped from the backend's
    // pre-ordered `logs` array by formatMeetingResponse.
    const logs = logPages?.entries ?? [];
    const totalItems = logPages?.pagination?.total ?? 0;
    const totalPages = logPages?.pagination?.total_pages ?? 1;

    return {
        logs,
        totalItems,
        totalPages,
        loading,
        isValidating,
        error,
        mutate,
        accountLinkRequired,
        setAccountLinkRequired,
    };
}
